import { useState } from 'react';
import { post } from 'aws-amplify/api';
import { useAuth } from '@/lib/auth/AuthContext';
import { useToast } from '@/hooks/use-toast';
import LoadingSpinner from '@/components/LoadingSpinner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

type Environment = 'prod' | 'uat';
type CleanupScope = 'conflictsOnly' | 'everything';
type CloudWatchLogQueryStatus = 'complete' | 'failed' | 'timeout' | 'noLogGroups' | 'error';

interface TokenRecord {
  userId: string;
  appVersion?: string;
  deviceArn?: string;
  pushToStartTokenArn?: string;
  deviceToken?: string;
  pushToStartToken?: string;
  pushToStartEnabled?: boolean;
  usedPushToStartTokenArns?: string[];
  environment?: Environment;
}

interface ArnReference {
  userId: string;
  environment: Environment;
  field: 'deviceArn' | 'pushToStartTokenArn' | 'usedPushToStartTokenArn';
  isPrimary: boolean;
}

interface SnsEndpointDiagnostics {
  arn: string;
  status: 'ok' | 'error';
  token?: string;
  enabled?: boolean;
  customUserData?: string;
  attributes?: Record<string, string>;
  errorMessage?: string;
  references: ArnReference[];
}

interface SubscriptionSummary {
  sports: string[];
  leagues: string[];
  teams: string[];
  events: string[];
}

interface SubscriptionEnvironmentDiagnostics {
  environment: Environment;
  tableName: string;
  records: Array<{ subscriptionId: string; userId: string }>;
  total: number;
  summary: SubscriptionSummary;
}

interface SubscriptionDiagnostics {
  total: number;
  summary: SubscriptionSummary;
  byEnvironment: SubscriptionEnvironmentDiagnostics[];
}

interface CloudWatchLogEntry {
  timestamp: string;
  message: string;
  logGroup: string;
  logStream?: string;
}

interface CloudWatchLogDiagnostics {
  status: CloudWatchLogQueryStatus;
  rangeStart: string;
  rangeEnd: string;
  queriedLogGroups: string[];
  entries: CloudWatchLogEntry[];
  queryId?: string;
  errorMessage?: string;
}

interface DeleteCount {
  attempted: number;
  successful: number;
}

interface SubscriptionDeletionResult {
  environment: Environment;
  tableName: string;
  attempted: number;
  successful: number;
}

interface CleanupSummary {
  scope: CleanupScope;
  conflicts: Record<Environment, DeleteCount>;
  userRecords: Record<Environment, DeleteCount>;
  subscriptions: SubscriptionDeletionResult[];
}

interface CleanUserIdResponse {
  requestedUserId: string;
  userIdItem?: TokenRecord;
  userRecords: TokenRecord[];
  prodItems: TokenRecord[];
  uatItems: TokenRecord[];
  snsEndpoints: SnsEndpointDiagnostics[];
  subscriptions: SubscriptionDiagnostics;
  cleanupPreview: { conflictsOnly: number; everything: number };
  deletionSummary?: CleanupSummary;
  diagnosticsGeneratedAt: string;
  cleanupScope: CleanupScope;
  cloudWatchLogs?: CloudWatchLogDiagnostics;
  message?: string;
}

const formatList = (items: string[]) => (items.length ? items.join(', ') : 'None');

const renderRecordsTable = (records: TokenRecord[]) => {
  if (!records.length) {
    return <p className="text-sm text-muted-foreground">No records found for this environment.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Environment</TableHead>
            <TableHead>User ID</TableHead>
            <TableHead>App Version</TableHead>
            <TableHead>Device ARN</TableHead>
            <TableHead>Push-to-Start ARN</TableHead>
            <TableHead>Device Token</TableHead>
            <TableHead>Push-to-Start Token</TableHead>
            <TableHead>Push-to-Start Enabled</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.map((record) => (
            <TableRow key={`${record.environment}-${record.userId}-${record.deviceArn}-${record.pushToStartTokenArn}`}>
              <TableCell>
                <Badge variant="outline">{record.environment?.toUpperCase() ?? 'N/A'}</Badge>
              </TableCell>
              <TableCell className="font-mono text-xs">{record.userId}</TableCell>
              <TableCell>{record.appVersion ?? '—'}</TableCell>
              <TableCell className="font-mono text-xs break-all">{record.deviceArn ?? '—'}</TableCell>
              <TableCell className="font-mono text-xs break-all">{record.pushToStartTokenArn ?? '—'}</TableCell>
              <TableCell className="font-mono text-xs break-all">{record.deviceToken ?? '—'}</TableCell>
              <TableCell className="font-mono text-xs break-all">{record.pushToStartToken ?? '—'}</TableCell>
              <TableCell>{record.pushToStartEnabled ? 'Enabled' : 'Disabled'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default function CleanUserId() {
  const { session } = useAuth();
  const { toast } = useToast();
  const [userId, setUserId] = useState('');
  const [diagnostics, setDiagnostics] = useState<CleanUserIdResponse | null>(null);
  const [rawResponse, setRawResponse] = useState('');
  const [cleanupScope, setCleanupScope] = useState<CleanupScope>('conflictsOnly');
  const [confirmCleanup, setConfirmCleanup] = useState(false);
  const [action, setAction] = useState<'idle' | 'diagnosing' | 'cleaning'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const sendRequest = async (deleteFlag: boolean, scope: CleanupScope) => {
    const trimmedUserId = userId.trim();
    if (!trimmedUserId) {
      throw new Error('User ID is required');
    }

    const response = post({
      apiName: 'dashboard',
      path: '/admin/cleanUserId',
      options: {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.tokens?.idToken}`,
        },
        withCredentials: true,
        body: {
          userId: trimmedUserId,
          deleteFlag,
          cleanupScope: scope,
        },
      },
    });

    const { body } = await response.response;
    const data = await body.json();

    if (data?.error) {
      throw new Error(data.error);
    }

    setDiagnostics(data);
    setRawResponse(JSON.stringify(data, null, 2));
    setConfirmCleanup(false);
    return data as CleanUserIdResponse;
  };

  const handleDiagnose = async () => {
    if (!userId.trim()) {
      toast({
        title: 'User ID required',
        description: 'Enter a user ID before running diagnostics.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setErrorMessage(null);
      setAction('diagnosing');
      const data = await sendRequest(false, cleanupScope);
      toast({
        title: 'Diagnostics complete',
        description: data.message ?? 'Fetched diagnostics successfully.',
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to run diagnostics';
      setErrorMessage(message);
      toast({ title: 'Diagnostics failed', description: message, variant: 'destructive' });
    } finally {
      setAction('idle');
    }
  };

  const handleCleanup = async () => {
    if (!diagnostics) {
      toast({ title: 'Diagnostics required', description: 'Run diagnostics before cleaning.', variant: 'destructive' });
      return;
    }

    const recordsTargeted = cleanupScope === 'conflictsOnly'
      ? diagnostics.cleanupPreview.conflictsOnly
      : diagnostics.cleanupPreview.everything;

    if (recordsTargeted === 0) {
      toast({ title: 'Nothing to clean', description: 'No matching records remain to delete.', variant: 'destructive' });
      return;
    }

    if (!confirmCleanup) {
      toast({
        title: 'Confirm cleanup',
        description: 'Check the confirmation box before running cleanup.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setErrorMessage(null);
      setAction('cleaning');
      const data = await sendRequest(true, cleanupScope);
      const deletionSummary = data.deletionSummary;
      const summaryText = deletionSummary
        ? `${deletionSummary.conflicts.prod.successful + deletionSummary.conflicts.uat.successful} conflicting records removed`
        : 'Cleanup completed';
      toast({ title: 'Cleanup complete', description: summaryText });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to clean user data';
      setErrorMessage(message);
      toast({ title: 'Cleanup failed', description: message, variant: 'destructive' });
    } finally {
      setAction('idle');
    }
  };

  const handleReset = () => {
    setDiagnostics(null);
    setRawResponse('');
    setConfirmCleanup(false);
    setErrorMessage(null);
  };

  const conflictingRecords = (diagnostics?.prodItems.length ?? 0) + (diagnostics?.uatItems.length ?? 0);
  const userRecordCount = diagnostics?.userRecords.length ?? (diagnostics?.userIdItem ? 1 : 0);
  const recordsTargeted = diagnostics
    ? cleanupScope === 'conflictsOnly'
      ? diagnostics.cleanupPreview.conflictsOnly
      : diagnostics.cleanupPreview.everything
    : 0;
  const isDiagnosing = action === 'diagnosing';
  const isCleaning = action === 'cleaning';
  const formatDateTime = (value: string) => new Date(value).toLocaleString();
  const describeCloudWatchStatus = (status: CloudWatchLogQueryStatus) => {
    switch (status) {
      case 'complete':
        return 'Complete';
      case 'noLogGroups':
        return 'No log groups';
      case 'timeout':
        return 'Timed out';
      case 'failed':
        return 'Query failed';
      case 'error':
      default:
        return 'Error';
    }
  };
  const getCloudWatchBadgeVariant = (status: CloudWatchLogQueryStatus) => {
    switch (status) {
      case 'complete':
        return 'secondary';
      case 'noLogGroups':
        return 'outline';
      default:
        return 'destructive';
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Clean User ID</h1>
          <p className="text-muted-foreground">Diagnose and reconcile push token records before cleaning.</p>
        </div>
        <Badge variant="outline">Diagnostic mode</Badge>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Run Diagnostics</CardTitle>
          <CardDescription>Fetch push token records, SNS metadata, and subscriptions for the supplied user ID.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="userId">User ID</Label>
            <Input
              id="userId"
              placeholder="Enter user ID"
              value={userId}
              onChange={(event) => setUserId(event.target.value)}
              disabled={isDiagnosing || isCleaning}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={handleDiagnose} disabled={isDiagnosing || !userId.trim()}>
              {isDiagnosing ? 'Running diagnostics...' : 'Run diagnostics'}
            </Button>
            <Button variant="secondary" onClick={handleReset} disabled={isDiagnosing || isCleaning || (!diagnostics && !rawResponse)}>
              Clear results
            </Button>
          </div>
          {diagnostics && (
            <p className="text-sm text-muted-foreground">
              Last run: {new Date(diagnostics.diagnosticsGeneratedAt).toLocaleString()}
            </p>
          )}
        </CardContent>
      </Card>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>Request failed</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Summary</CardTitle>
          <CardDescription>High-level diagnostics for the last request.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isDiagnosing ? (
            <div className="py-6">
              <LoadingSpinner size="sm" message="Running diagnostics..." />
            </div>
          ) : diagnostics ? (
            <>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div>
                  <p className="text-sm text-muted-foreground">User records</p>
                  <p className="text-2xl font-semibold">{userRecordCount}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Conflicts detected</p>
                  <p className="text-2xl font-semibold">{conflictingRecords}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Subscriptions</p>
                  <p className="text-2xl font-semibold">{diagnostics.subscriptions.total}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">SNS endpoints</p>
                  <p className="text-2xl font-semibold">{diagnostics.snsEndpoints.length}</p>
                </div>
              </div>
              {diagnostics.message && (
                <Alert>
                  <AlertTitle>Server summary</AlertTitle>
                  <AlertDescription>{diagnostics.message}</AlertDescription>
                </Alert>
              )}
              {!diagnostics.userRecords.length && (
                <Alert>
                  <AlertTitle>No push token record found</AlertTitle>
                  <AlertDescription>
                    The user ID does not have an active push token entry. You can still inspect subscriptions or SNS endpoints.
                  </AlertDescription>
                </Alert>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Run diagnostics to see a summary.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>User Records</CardTitle>
          <CardDescription>Active push token entries for the requested user.</CardDescription>
        </CardHeader>
        <CardContent>{diagnostics ? renderRecordsTable(diagnostics.userRecords) : <p className="text-sm text-muted-foreground">No diagnostics available yet.</p>}</CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Conflicting Records</CardTitle>
          <CardDescription>Other records sharing tokens or ARNs with the selected user.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="mb-2 text-sm font-semibold">Production</h3>
            {diagnostics ? renderRecordsTable(diagnostics.prodItems) : <p className="text-sm text-muted-foreground">No diagnostics available yet.</p>}
          </div>
          <div>
            <h3 className="mb-2 text-sm font-semibold">UAT</h3>
            {diagnostics ? renderRecordsTable(diagnostics.uatItems) : <p className="text-sm text-muted-foreground">No diagnostics available yet.</p>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>CloudWatch References</CardTitle>
          <CardDescription>Searches ApiGateway router and queue worker logs for the user ID over the last 24 hours.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {diagnostics ? (
            diagnostics.cloudWatchLogs ? (
              <>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm text-muted-foreground">Lookback window</p>
                    <p className="text-sm font-medium">
                      {formatDateTime(diagnostics.cloudWatchLogs.rangeStart)} – {formatDateTime(diagnostics.cloudWatchLogs.rangeEnd)}
                    </p>
                  </div>
                  <Badge variant={getCloudWatchBadgeVariant(diagnostics.cloudWatchLogs.status)}>
                    {describeCloudWatchStatus(diagnostics.cloudWatchLogs.status)}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Queried {diagnostics.cloudWatchLogs.queriedLogGroups.length} log group{diagnostics.cloudWatchLogs.queriedLogGroups.length === 1 ? '' : 's'} and
                  {' '}found {diagnostics.cloudWatchLogs.entries.length} matching entr{diagnostics.cloudWatchLogs.entries.length === 1 ? 'y' : 'ies'}.
                </p>
                {diagnostics.cloudWatchLogs.status === 'noLogGroups' && (
                  <Alert>
                    <AlertTitle>No matching log groups</AlertTitle>
                    <AlertDescription>
                      The configured patterns did not match any log groups in this account/region.
                    </AlertDescription>
                  </Alert>
                )}
                {['failed', 'timeout', 'error'].includes(diagnostics.cloudWatchLogs.status) && diagnostics.cloudWatchLogs.errorMessage && (
                  <Alert variant="destructive">
                    <AlertTitle>CloudWatch query issue</AlertTitle>
                    <AlertDescription>{diagnostics.cloudWatchLogs.errorMessage}</AlertDescription>
                  </Alert>
                )}
                {diagnostics.cloudWatchLogs.entries.length ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Timestamp</TableHead>
                          <TableHead>Log Group</TableHead>
                          <TableHead>Log Stream</TableHead>
                          <TableHead>Message</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {diagnostics.cloudWatchLogs.entries.map((entry, index) => (
                          <TableRow key={`${entry.logGroup}-${entry.logStream}-${entry.timestamp}-${index}`}>
                            <TableCell className="font-mono text-xs whitespace-nowrap">
                              {formatDateTime(entry.timestamp)}
                            </TableCell>
                            <TableCell className="font-mono text-xs break-all">{entry.logGroup}</TableCell>
                            <TableCell className="font-mono text-xs break-all">{entry.logStream ?? '—'}</TableCell>
                            <TableCell className="font-mono text-xs whitespace-pre-wrap break-all">
                              {entry.message || '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : diagnostics.cloudWatchLogs.status === 'complete' ? (
                  <p className="text-sm text-muted-foreground">No log entries mentioned this user in the last day.</p>
                ) : null}
                {diagnostics.cloudWatchLogs.queriedLogGroups.length > 0 && (
                  <details className="text-sm">
                    <summary className="cursor-pointer text-muted-foreground">Log groups scanned</summary>
                    <ul className="mt-2 space-y-1 font-mono text-xs">
                      {diagnostics.cloudWatchLogs.queriedLogGroups.map((group) => (
                        <li key={group} className="break-all">{group}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">No CloudWatch data returned for this run.</p>
            )
          ) : (
            <p className="text-sm text-muted-foreground">Run diagnostics to see CloudWatch matches.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>SNS Endpoint Verification</CardTitle>
          <CardDescription>Inspect endpoint attributes, status, and attached user data.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {diagnostics?.snsEndpoints.length ? (
            diagnostics.snsEndpoints.map((endpoint) => (
              <div key={endpoint.arn} className="rounded-md border p-4 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-mono text-xs break-all">{endpoint.arn}</p>
                  <Badge variant={endpoint.status === 'ok' ? 'secondary' : 'destructive'}>
                    {endpoint.status === 'ok' ? 'Verified' : 'Error'}
                  </Badge>
                </div>
                {endpoint.status === 'ok' ? (
                  <div className="grid gap-2 md:grid-cols-2">
                    <p className="text-sm text-muted-foreground">Token: <span className="font-mono text-xs break-all text-foreground">{endpoint.token ?? '—'}</span></p>
                    <p className="text-sm text-muted-foreground">Enabled: {endpoint.enabled === undefined ? 'Unknown' : endpoint.enabled ? 'Yes' : 'No'}</p>
                    <p className="text-sm text-muted-foreground">User data: {endpoint.customUserData ?? '—'}</p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{endpoint.errorMessage ?? 'Unable to fetch attributes.'}</p>
                )}
                <div>
                  <p className="text-sm font-semibold">References</p>
                  <div className="flex flex-wrap gap-2">
                    {endpoint.references.map((reference, index) => (
                      <Badge key={`${endpoint.arn}-${reference.userId}-${reference.field}-${index}`} variant="outline">
                        {reference.userId} • {reference.environment.toUpperCase()} • {reference.field}
                        {reference.isPrimary ? ' • target' : ''}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground">No SNS endpoints reported for this user.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Subscriptions</CardTitle>
          <CardDescription>Current subscription scope grouped by environment.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {diagnostics ? (
            diagnostics.subscriptions.byEnvironment.map((env) => (
              <div key={env.environment} className="rounded-md border p-4 space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{env.environment.toUpperCase()}</Badge>
                    <span className="text-sm text-muted-foreground">{env.tableName}</span>
                  </div>
                  <span className="text-sm font-medium">{env.total} record{env.total === 1 ? '' : 's'}</span>
                </div>
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Sports</p>
                    <p>{formatList(env.summary.sports)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Leagues</p>
                    <p>{formatList(env.summary.leagues)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Teams</p>
                    <p>{formatList(env.summary.teams)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Events</p>
                    <p>{formatList(env.summary.events)}</p>
                  </div>
                </div>
                {env.records.length > 0 && (
                  <details className="text-sm">
                    <summary className="cursor-pointer text-muted-foreground">Show subscription IDs</summary>
                    <ul className="mt-2 space-y-1 font-mono text-xs">
                      {env.records.map((record) => (
                        <li key={`${env.environment}-${record.subscriptionId}-${record.userId}`} className="break-all">
                          {record.subscriptionId}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground">Run diagnostics to view subscriptions.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Cleanup Actions</CardTitle>
          <CardDescription>Select what to delete and confirm before running cleanup.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Cleanup scope</Label>
              <Select value={cleanupScope} onValueChange={(value) => setCleanupScope(value as CleanupScope)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select scope" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="conflictsOnly">Conflicting records only</SelectItem>
                  <SelectItem value="everything">Everything (conflicts + user + subscriptions)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Records targeted</Label>
              <p className="text-2xl font-semibold">{recordsTargeted}</p>
              <p className="text-sm text-muted-foreground">Based on the latest diagnostics.</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="confirmCleanup"
              checked={confirmCleanup}
              onCheckedChange={(value) => setConfirmCleanup(value === true)}
              disabled={!diagnostics || recordsTargeted === 0 || isCleaning}
            />
            <Label htmlFor="confirmCleanup">I understand this action will permanently delete the selected records.</Label>
          </div>
          <Button
            variant="destructive"
            onClick={handleCleanup}
            disabled={!diagnostics || recordsTargeted === 0 || isCleaning}
          >
            {isCleaning ? 'Cleaning...' : cleanupScope === 'everything' ? 'Clean everything' : 'Clean conflicts'}
          </Button>
          {diagnostics?.deletionSummary && (
            <div className="rounded-md border p-4 text-sm">
              <p className="font-medium">Last cleanup summary</p>
              <ul className="mt-2 space-y-1 text-muted-foreground">
                <li>
                  Conflicts removed: {diagnostics.deletionSummary.conflicts.prod.successful + diagnostics.deletionSummary.conflicts.uat.successful}
                  {' '}of {diagnostics.deletionSummary.conflicts.prod.attempted + diagnostics.deletionSummary.conflicts.uat.attempted}
                </li>
                <li>
                  User records removed: {diagnostics.deletionSummary.userRecords.prod.successful + diagnostics.deletionSummary.userRecords.uat.successful}
                </li>
                <li>
                  Subscription rows removed: {diagnostics.deletionSummary.subscriptions.reduce((total, item) => total + item.successful, 0)}
                </li>
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Raw Response</CardTitle>
          <CardDescription>The exact payload returned by the API.</CardDescription>
        </CardHeader>
        <CardContent>
          {rawResponse ? (
            <textarea
              className="h-48 w-full resize-none rounded-md border bg-muted p-2 font-mono text-xs"
              value={rawResponse}
              readOnly
            />
          ) : (
            <p className="text-sm text-muted-foreground">Run diagnostics to see the raw API response.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
